/**
 * A área da paciente no computador, dentro de uma moldura de celular
 * (15/10/2026). O app foi desenhado para o telefone; numa tela larga ele
 * esticava. No computador, `/eu` abre uma casca leve — só o aparelho, sobre
 * o fundo da marca — com o próprio app num quadro (iframe) do tamanho de um
 * iPhone: lá dentro a tela "acha" que é um celular — barras fixas, folhas,
 * alturas de tela inteira, tudo como no telefone, sem mexer em cada tela.
 *
 * Só no computador (tela larga, mouse), só em `/eu`, nunca dentro do próprio
 * quadro.
 */

/** A partir desta largura, com mouse, é computador. */
export const LARGURA_DO_COMPUTADOR = 1024;
/** A mensagem que o app de dentro manda à casca a cada troca de tela. */
export const MENSAGEM_DE_ROTA = 'eva:rota';

export interface Ambiente {
  largura: number;
  ponteiroFino: boolean;
  caminho: string;
  emQuadro: boolean;
}

/** A regra, pura: casca com moldura ou o app de sempre. */
export function decidirMoldura(a: Ambiente): boolean {
  if (a.emQuadro) return false;
  if (!(a.caminho === '/eu' || a.caminho.startsWith('/eu/'))) return false;
  return a.largura >= LARGURA_DO_COMPUTADOR && a.ponteiroFino;
}

export function estaNoQuadro(): boolean {
  try {
    return typeof window !== 'undefined' && window.self !== window.top;
  } catch {
    return true; // acesso ao topo negado: é quadro de outra origem
  }
}

/** Decidido uma vez, ao carregar: a casca não vira app nem o app vira casca no meio. */
export const EM_MOLDURA: boolean =
  typeof window !== 'undefined' &&
  decidirMoldura({
    largura: window.innerWidth,
    ponteiroFino: !!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches,
    caminho: window.location.pathname,
    emQuadro: estaNoQuadro(),
  });

interface Roteador {
  state: { location: { pathname: string; search: string; hash: string } };
  subscribe: (fn: (estado: { location: { pathname: string; search: string; hash: string } }) => void) => () => void;
}

/** Dentro do quadro: conta à casca para onde o app foi, para a barra de endereço acompanhar. */
export function avisarRotaACasca(r: Roteador): void {
  if (!estaNoQuadro()) return;
  const avisar = (l: { pathname: string; search: string; hash: string }) => {
    try {
      window.parent.postMessage({ tipo: MENSAGEM_DE_ROTA, caminho: `${l.pathname}${l.search}${l.hash}` }, window.location.origin);
    } catch {
      /* casca de outra origem: não é a nossa */
    }
  };
  avisar(r.state.location);
  r.subscribe((estado) => avisar(estado.location));
}
